import { useQuery } from '@apollo/client'

import { PaymentProvidersQueryDocument } from '__generated__/atlas-types'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'

import { atlasClient } from 'graphql/client'
import { RestEndpoints, restClient } from 'graphql/restClient'
import { atlas, rest } from 'graphql/tags'

import { GetRatesQuery, GetRatesQueryVariables } from '../../types'

export const GET_RATES_QUERY = rest`#graphql
  query GetRatesQuery($merchantId: ID!) {
    rates(merchantId: $merchantId) @rest(type: "RateOffer", path: "getRates?MerchantId={args.merchantId}", endpoint: ${RestEndpoints.Reach}) {
      RateOffers {
        Id
        Currency
        Expiry
        Rate
      }
    }
  }
`

type UseGetRatesQueryParams = {
  currencyCode?: string
  externalPayment?: {
    brandId: string
    id: string
    paymentProvider?: { merchantId?: string | null } | null
    retailerName: string
  }
  order?: {
    id: string
    currencyCode?: string
    retailer?: { id: string }
    brand: { id: string }
  }
  providersData?: {
    accountPaymentProviders: {
      paymentProvider: string
      connectionDetails?: { merchantId?: string } | null
    }[]
  }
  shouldUseLocalCurrencies: boolean
}

export const useGetRatesQuery = ({
  currencyCode,
  externalPayment,
  order,
  providersData,
  shouldUseLocalCurrencies,
}: UseGetRatesQueryParams) => {
  const { trackEvent } = useUserAnalytics()

  return useQuery<GetRatesQuery, GetRatesQueryVariables>(GET_RATES_QUERY, {
    variables: {
      merchantId:
        providersData?.accountPaymentProviders.find(
          (provider) => provider.paymentProvider === 'reach',
        )?.connectionDetails?.merchantId ||
        externalPayment?.paymentProvider?.merchantId ||
        '',
    },
    client: restClient,
    skip: !shouldUseLocalCurrencies || (order && !providersData),
    onError: () => {
      trackEvent('PAYMENTS - GET RATES FAILED', {
        merchantId: providersData?.accountPaymentProviders.find(
          (provider) => provider.paymentProvider === 'reach',
        )?.connectionDetails?.merchantId,
        orderId: order?.id,
        currency: currencyCode,
        brandId: order?.brand.id ?? externalPayment?.brandId,
        retailerId: order?.retailer?.id ?? externalPayment?.retailerName,
        externalPaymentId: externalPayment?.id,
      })
    },
  })
}

export const PAYMENT_PROVIDERS_QUERY = atlas`#graphql
  query PaymentProvidersQuery($destinationAccountId: ID!) {
    accountPaymentProviders(destinationAccountId: $destinationAccountId) {
      paymentProvider
      connectionDetails {
        merchantId
      }
    }
  }
`

type UsePaymentProvidersQueryParams = {
  orderDestinationAccountId?: string
  retailerId?: string
  shouldUseLocalCurrencies: boolean
  uuid?: string
}

export const usePaymentProvidersQuery = ({
  orderDestinationAccountId,
  retailerId,
  shouldUseLocalCurrencies,
  uuid,
}: UsePaymentProvidersQueryParams) =>
  useQuery(PaymentProvidersQueryDocument, {
    variables: { destinationAccountId: orderDestinationAccountId! },
    skip:
      !shouldUseLocalCurrencies || !orderDestinationAccountId || !retailerId,
    client: atlasClient,
    fetchPolicy: 'cache-first',
    ...(uuid && {
      context: {
        headers: {
          'invoice-auth': uuid,
        },
      },
    }),
  })
